import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { ArrowLeft, FileText, Calendar, Building2, Calculator, Clock, Hash, CreditCard, Eye, Loader2, IndianRupee, ShieldCheck, Receipt, Wallet, TrendingUp } from "lucide-react";
import { format, subDays, startOfDay } from "date-fns";
import { parseLocalDateInput } from "../../lib/dateFormat";
import { NoticeOfAssignment as NoAType, LenderBankAccount, ScheduleLine } from "../../types/schema";
import NoAPdfDrawer from "../../components/noa/NoAPdfDrawer";
import PVCalculationModal from "../../components/noa/PVCalculationModal";
import SelectedRentalsModal from "../../components/noa/SelectedRentalsModal";
import apiService from "../../services/apiService";
import { Invoice } from "../../types/invoice";
import NoaNachSetupModal from "../../components/noa/NoaNachSetupModal";
import NoAVisualisePreviewModal from "../../components/noa/NoAVisualisePreviewModal";
import NoAApproveModal, { NoAApproveConfirmPayload } from "./NoAApproveModal";
import { toast } from "react-toastify";
import { determineGSTSplit } from "../../lib/gst-rules";
import { cn } from "../../lib/utils";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

const getStatusColor = (status: string) => {
  switch (status) {
    case "Signed": return "bg-green-100 text-green-800 border-green-200";
    case "Approved": return "bg-blue-100 text-blue-800 border-blue-200";
    case "Submit": return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "Draft": return "bg-gray-100 text-gray-800 border-gray-200";
    default: return "bg-gray-100 text-gray-800 border-gray-200";
  }
};


const getFundingModeLabel = (mode: string) => {
  switch (mode) {
    case "A": return "Pre GST";
    case "B": return "Post GST";
    case "C": return "ETC";
    default: return mode;
  }
};

interface NoaFundingCalculations {
  disbursementAmount: number;
  securityDeposit: number;
  lenderProcessingFee: number;
  customerProcessingFee: number;
  interimRental: number;
  totalAssetValue: number;
  upfrontMargin: number;
}

const toFundingNum = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeFundingCalculations = (raw: any): NoaFundingCalculations => ({
  disbursementAmount: toFundingNum(raw?.disbursementAmount ?? raw?.disbursement_amount),
  securityDeposit: toFundingNum(raw?.securityDeposit ?? raw?.security_deposit),
  customerProcessingFee: toFundingNum(raw?.customerProcessingFee ?? raw?.customer_processing_fee),
  lenderProcessingFee: toFundingNum(raw?.lenderProcessingFee ?? raw?.lender_processing_fee),
  interimRental: toFundingNum(raw?.interimRental ?? raw?.interim_rental),
  totalAssetValue: toFundingNum(raw?.totalAssetValue ?? raw?.total_asset_value),
  upfrontMargin: toFundingNum(
    raw?.upfrontMargin ?? raw?.upfront_margin ?? raw?.upfrontRevenue ?? raw?.upfront_revenue
  ),
});

const formatFundingCurrency = (value: number) =>
  `₹${value.toLocaleString("en-IN")}`;

interface ViewNoticeOfAssignmentProps {
  noa: NoAType | null;
  onBack?: () => void;
  onNoAUpdated?: (noa: NoAType) => void;
}

export default function ViewNoticeOfAssignment({
  noa: initialNoa,
  onBack,
  onNoAUpdated,
}: ViewNoticeOfAssignmentProps) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const [noa, setNoa] = useState<NoAType | null>(initialNoa);

  useEffect(() => {
    setNoa(initialNoa);
  }, [initialNoa]);
  const navigate = useNavigate();
  const [pdfDrawerOpen, setPdfDrawerOpen] = useState(false);
  const [rentalsModalOpen, setRentalsModalOpen] = useState(false);
  const [nachModalOpen, setNachModalOpen] = useState(false);
  const [noaStatus, setNoaStatus] = useState<string | undefined>(noa?.status);
  const [noaNachActive, setNoaNachActive] = useState<boolean | undefined>(noa?.noaNachActive || false);
  const [noaNachStartPeriod, setNoaNachStartPeriod] = useState<number | string | undefined>(noa?.noaNachStartPeriod);
  const [noaNachType, setNoaNachType] = useState<"full" | "base_only" | undefined>(noa?.noaNachType);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [fundingCalculations, setFundingCalculations] =
    useState<NoaFundingCalculations | null>(null);
  const [fundingCalculationsLoading, setFundingCalculationsLoading] =
    useState(false);
  const [visualiseOpen, setVisualiseOpen] = useState(false);


  // Sync local status with prop when noa changes
  useEffect(() => {

    setNoaStatus(noa?.status);
    setNoaNachActive(noa?.noaNachActive);
    setNoaNachStartPeriod(noa?.noaNachStartPeriod);
    setNoaNachType(noa?.noaNachType);
  }, [noa?.status, noa?.noaNachActive, noa?.noaNachStartPeriod, noa?.noaNachType]);


  const getStatusColorTwo = (status: string) => {
    switch (status) {
      case "Draft": return "secondary";
      case "Submitted": return "warning";
      case "Approved": return "warning";
      case "In Signing": return "info";
      case "Assigned": return "success";
      case "Matured": return "destructive";
      case "Active": return "success";
      default: return "secondary";
    }
  };

  const refreshNoaData = async () => {
    const customerId = noa?.customer?.customerId;
    if (!customerId || !noa?.noaId) return;

    try {
      const response = await apiService.get<{ data: NoAType[] }>(
        `/noa/get-noas?customerId=${customerId}`
      );
      const updated = (response.data || []).find(
        (n) =>
          n.noaId === noa.noaId ||
          (noa.id != null && String(n.id) === String(noa.id))
      );
      if (updated) {
        setNoa(updated);
        setNoaStatus(updated.status);
        setNoaNachActive(updated.noaNachActive);
        setNoaNachStartPeriod(updated.noaNachStartPeriod);
        setNoaNachType(updated.noaNachType);
        onNoAUpdated?.(updated);
        await fetchFundingCalculations(updated.noaId);
      }
    } catch (error) {
      console.error("Error refreshing NoA:", error);
    }
  };

  const fetchFundingCalculations = async (noaId: string) => {
    setFundingCalculationsLoading(true);
    try {
      const response = await apiService.get<{ data?: any } | any>(
        `/noa/get-funding-calculations/${noaId}`
      );
      const payload = (response as any)?.data ?? response;
      setFundingCalculations(normalizeFundingCalculations(payload));
    } catch (error) {
      console.error("Failed to fetch funding calculations:", error);
      setFundingCalculations(null);
    } finally {
      setFundingCalculationsLoading(false);
    }
  };

  useEffect(() => {
    if (!noa?.noaId) {
      setFundingCalculations(null);
      return;
    }
    fetchFundingCalculations(noa.noaId);
  }, [noa?.noaId]);

  // Find NoA by ID or use mock data if not found
  // const noa = noAs.find(n => n.noaId === noaId) || mockNoAData;
  // const schedules = getApprovedSchedules();
  const linkedSchedule = noa?.rentalSchedule;
  const canVisualise = Boolean(
    linkedSchedule?.lines?.length || noa?.selectedRentals?.length
  );

  // Distinct productType and requirementId from noa.rentalSchedule.assets (for Linked Rental Schedule section)
  const scheduleAssets = useMemo(
    () => noa?.rentalSchedule?.assets ?? noa?.rentalSchedule?.asset ?? [],
    [noa?.rentalSchedule?.assets, noa?.rentalSchedule?.asset]
  );
  const distinctProductTypes = useMemo(() => {
    const values = (scheduleAssets as any[])
      .map((a: any) => a.productType ?? a.productTypeName)
      .filter((v: any) => v != null && v !== "");
    return [...new Set(values)];
  }, [scheduleAssets]);
  const distinctRequirementIds = useMemo(() => {
    const values = (scheduleAssets as any[])
      .map((a: any) => a.requirementId)
      .filter((v: any) => v != null && v !== "");
    return [...new Set(values)];
  }, [scheduleAssets]);

  // Generate invoices from NOA when approved
  const generateInvoicesFromNoA = async (
    noaData: NoAType,
    updatedScheduleLines: ScheduleLine[],
    approvalMeta?: {
      collectionRoute: NoAApproveConfirmPayload["collectionRoute"];
      lenderBankAccount: LenderBankAccount;
      lenderBankAccountId: string;
    }
  ): Promise<Invoice[]> => {
    if (!noaData.rentalSchedule || !noaData.rentalSchedule.lines) {
      throw new Error("Rental schedule data is required to generate invoices");
    }

    const schedule = noaData.rentalSchedule;
    const fundingType = schedule.config.fundingType;
    const newInvoices: Invoice[] = [];

    const scheduleLines = updatedScheduleLines || schedule.lines;

    // Extract scheduleLineId values from selectedRentals
    // selectedRentals can be an array of objects with scheduleLineId property or array of strings
    const selectedScheduleLineIds = noaData.selectedRentals?.map((item: any) => {
      // If item is an object with scheduleLineId property, extract it
      if (typeof item === 'object' && item !== null && 'scheduleLineId' in item) {
        return String(item.scheduleLineId);
      }
      // If item is already a string/number, use it directly
      return String(item);
    }) || [];


    const chkInterim = scheduleLines.some((line: any) => line.isInterim === true);

    // Create invoices for all schedule lines
    scheduleLines.forEach((line: any) => {
      // Check if this line's scheduleLineId is in the selectedRentals
      const isAssignedToLender = selectedScheduleLineIds.includes(String(line.scheduleLineId));
      // Determine invoice amounts based on funding type and assignment
      let baseAmount = line.baseRentRounded;
      let gstRawAmount = line.gstRaw;
      let tdsAmount = line.tdsRounded;
      let grandTotal = line.totalRaw;

      // If assigned to lender, follow funding type logic
      if (isAssignedToLender) {
        if (fundingType === 'Pre GST') {
          // Lender gets: Base only
          grandTotal = baseAmount;
          tdsAmount = 0;
        } else if (fundingType === 'Post GST') {
          // Lender gets: Base + GST
          grandTotal = baseAmount + gstRawAmount;
          tdsAmount = 0;
        } else if (fundingType === 'Post TDS') {
          // Lender gets: Base + GST - TDS
          grandTotal = line.totalRaw;
        }
      } else {
        // Not assigned: Goes to Astrafin (Base + GST - TDS)
        grandTotal = line.totalRaw;
      }

      let displayPeriodNo = line.periodNo;

      if (chkInterim) {
        if (line.periodNo === 'i') {
          displayPeriodNo = 1;
        } else {
          displayPeriodNo = Number(line.periodNo) + 1;
        }
      } else {
        displayPeriodNo = line.periodNo;
      }

      const gstSplit = determineGSTSplit(schedule.address.billTo.gstin, gstRawAmount);

      const invoice: Invoice = {
        invoiceId: '1',
        displayId: displayPeriodNo,
        rsInvoiceCode: `INV-${noaData.scheduleId}-R${line.periodNo}`,
        scheduleId: noaData.scheduleId,
        customerId: schedule.config.customer.customerId,
        customer: schedule.config.customer,
        type: line.isInterim ? "Advance" : "Rental",
        isManual: false, // Auto-generated from schedule
        invoiceDate: (() => {
          if (!line.dueDate) return line.fromDate;
          const today = startOfDay(new Date());
          const due = startOfDay(new Date(line.dueDate));
          // 5-day window ending on due date: [due - 4 days, due]
          // e.g. due 5 Sep → window 1–5 Sep
          const windowStart = subDays(due, 4);

          if (today.getTime() < windowStart.getTime()) {
            return format(windowStart, "yyyy-MM-dd");
          }
          if (today.getTime() > due.getTime()) {
            return format(due, "yyyy-MM-dd");
          }
          return format(today, "yyyy-MM-dd");
        })(),
        dueDate: line.dueDate,
        baseRounded: baseAmount,
        gstRounded: Math.ceil(gstRawAmount),
        gstType: gstSplit.gstType,
        igstAmount: gstSplit.igst,
        cgstAmount: gstSplit.cgst,
        sgstAmount: gstSplit.sgst,
        utgstAmount: gstSplit.utgst,
        gstRate: line.gstRate,
        tdsRounded: tdsAmount,
        grandTotalRounded: Math.ceil(grandTotal),
        totalRoundedAfterTDS: Math.ceil(line.totalRaw - line.tdsRounded),
        lineItems: [
          {
            id: `LINE-R${line.periodNo}-${noaData.scheduleId}`,
            scheduleID: noaData.scheduleId,
            scheduleLineID: line.scheduleLineId,
            description: `${line.isInterim ? 'Interim ' : ''}Rental - Period ${line.periodNo} (${line.fromDate} to ${line.toDate})`,
            baseAmount: baseAmount,
            gstAmount: gstRawAmount,
            tdsAmount: tdsAmount,
            totalAmount: grandTotal,
            fromDate: line.fromDate,
            toDate: line.toDate,
          },
        ],
        payments: [],
        status: "Pending",
        assignedToLender: isAssignedToLender,
        noaId: noaData.noaId,
        lenderName: isAssignedToLender ? noaData.lender : undefined,
        scheduleLineId: line.scheduleLineId,
        ...(isAssignedToLender && approvalMeta
          ? {
            collectionRoute: approvalMeta.collectionRoute,
            lenderBankAccountId: approvalMeta.lenderBankAccountId,
            lenderAccountHolderName: approvalMeta.lenderBankAccount.accountHolderName,
            lenderAccountNumber: approvalMeta.lenderBankAccount.accountNumber,
            lenderAccountType: approvalMeta.lenderBankAccount.accountType,
            lenderBankName: approvalMeta.lenderBankAccount.bankName,
            lenderBranchName: approvalMeta.lenderBankAccount.branchName,
            lenderIfscCode: approvalMeta.lenderBankAccount.ifscCode,
          }
          : {}),
      };
      newInvoices.push(invoice);
    });

    return newInvoices;
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!noa?.noaId) {
      toast.error("NoA ID is required");
      return;
    }

    if (newStatus === 'Approved') {
      setApproveModalOpen(true)
      return;
    }

    try {
      const response = await apiService.put<{ data: NoAType; message?: string }>(
        `/noa/update-noa-status/${noa?.noaId}`,
        { status: newStatus } as any
      );

      if (response.data) {
        setNoaStatus(newStatus);
        toast.success(response.message || "NoA status updated successfully");
      }
    } catch (error) {
      console.error("Error updating NoA status:", error);
      toast.error("Failed to update NoA status");
    }
  };


  const handleApprovalConfirm = async (payload: NoAApproveConfirmPayload) => {
    if (!noa) {
      toast.error("NoA data not available");
      throw new Error("NoA data not available");
    }

    const {
      disbursementDate,
      disbursementAmount,
      updatedScheduleLines,
      interimLine,
      collectionRoute,
      lenderBankAccountId,
      lenderBankAccount,
    } = payload;

    setIsApproving(true);
    try {
      const invoices = await generateInvoicesFromNoA(noa, updatedScheduleLines, {
        collectionRoute,
        lenderBankAccountId,
        lenderBankAccount,
      });

      await apiService.post<{ message?: string; data?: any }>(
        `/rental-schedule/create-rental-schedule-invoice`,
        {
          noaId: noa.noaId,
          invoices: invoices,
          disbursementDate: disbursementDate,
          disbursementAmount: disbursementAmount,
          customerId: noa.customer.customerId,
          scheduleId: noa.scheduleId,
          requirementId: distinctRequirementIds[0],
          lenderCompanyId: noa.lenderCompanyId,
          interimLine: interimLine,
          collection_route: collectionRoute,
          lender_bank_account_id: lenderBankAccountId,
        } as any
      );

      setNoaStatus("Approved");
      await refreshNoaData();

      toast.success(
        `NoA approved successfully. ${invoices.length} invoice(s) generated.`
      );
    } catch (error) {
      console.error("Error approving NoA:", error);
      toast.error("Failed to approve NoA");
      throw error;
    } finally {
      setIsApproving(false);
      await refreshWorkflow();
    }
  };

  // Get selected rentals from the linked schedule
  const getSelectedRentals = () => {
    if (!noa?.selectedRentals) return [];

    return noa?.selectedRentals.map((line: any) => {
      return {
        ...line,
        periodNo: line.periodNo || 0,
        fromDate: line.fromDate || "",
        toDate: line.toDate || "",
        dueDate: line.dueDate || "",
        baseRentRounded: line.baseRentRounded || 0,
        gstRaw: line.gstRaw || 0,
        total: line.totalRounded || 0
      };
    });
  };

  const selectedRentals = getSelectedRentals();

  // For PV: partial (P) rows use lenderShare as the cashflow (GST/TDS zeroed)
  const linesForPV = useMemo(
    () =>
      selectedRentals.map((line: any) => {
        const status = String(line.assignedStatus ?? "").trim().toUpperCase();
        if (status !== "P") return line;
        const share = Number(line.lenderShare ?? 0);
        return {
          ...line,
          baseRentRounded: share,
          baseRentRaw: share,
          gstRaw: 0,
          gstRounded: 0,
          gst12Rounded: 0,
          gst18Rounded: 0,
          gst28Rounded: 0,
          tdsRounded: 0,
          tdsRaw: 0,
        };
      }),
    [selectedRentals]
  );

  const lenderFundsGST =
    noa?.fundingMode === "B" || noa?.fundingMode === "C";
  const lenderFundsTDS = noa?.fundingMode === "C";
  // Under partial rows GST/TDS are already zeroed on the line clone above.

  // Handle NACH Setup save
  const handleNachSave = async (nachActive: boolean, nachStartPeriod: number | string, nachType: "full" | "base_only", dueDate: string) => {
    if (!noa?.noaId) {
      toast.error("NoA ID is required");
      return;
    }

    try {
      const updates = {
        noaNachStartPeriod: nachStartPeriod,
        noaNachType: nachType,
        noaNachStartDate: dueDate,
        noaNachConfiguredAt: new Date().toISOString(),
        noaNachActive: true,
        // noaNachConfiguredBy: currentUserId, // Add if you have user context
      };

      const response = await apiService.put<{ data: NoAType; message?: string }>(
        `/noa/update-noa-nach-setup/${noa.noaId}`,
        updates as any
      );

      if (response.data) {
        // Update local state immediately to reflect UI changes
        setNoaNachActive(true);
        setNoaNachStartPeriod(nachStartPeriod);
        setNoaNachType(nachType);
        toast.success(response.message || "NACH settings updated successfully");
        setNachModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating NACH settings:", error);
      toast.error("Failed to update NACH settings");
    }
  };

  const getDisplayStatus = (noa: any) => {
    const today = new Date();
    const lastDate = noa?.last_to_date ? new Date(noa.last_to_date) : null;
    const status = noa.status?.toLowerCase() ?? "draft";

    if (status === "draft") return "Draft";

    if (status === "approved") {
      if (lastDate) {
        if (today < lastDate) return "Active";
        return "Matured";
      }
      return "Approved";
    }

    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const displayStatus = getDisplayStatus(noa);

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <section className="pb-6 border-b space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                <FileText className="h-7 w-7" />
                {noa?.noaId}
              </h1>
              <Badge variant={
                getStatusColorTwo(displayStatus)}>
                {displayStatus}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                {noa?.customer.name}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Updated: {format(new Date(noa?.updatedAt || ""), "MMM dd, yyyy")}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Status Progression Buttons */}
            {noaStatus === "Draft" && (
              <Button onClick={() => handleStatusUpdate("Submit")}>
                Submit for Approval
              </Button>
            )}
            {noaStatus === "Submit" && (
              <Button onClick={() => handleStatusUpdate("Approved")} disabled={isApproving}>
                Approve
              </Button>
            )}
            {/* {noaStatus === "Approved" && (
              <Button onClick={() => handleStatusUpdate("Signed")}>
                Mark as Signed
              </Button>
            )} */}

            {/* View NOA hidden — DOCX download moved to Visualise preview modal */}
            {/* <Button
              variant="outline"
              onClick={() => setSystemNoaDrawerOpen(true)}
            >
              <FileText className="h-4 w-4 mr-2" />
              View NOA
            </Button> */}

            <Button
              variant="outline"
              onClick={() => setVisualiseOpen(true)}
              disabled={!canVisualise}
              title={!canVisualise ? "Schedule not linked" : "Visualise assignment against schedule"}
            >
              <Eye className="h-4 w-4 mr-2" />Visualise
            </Button>
            {/* <Button variant="outline" onClick={() => navigate('/noa')}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back
            </Button> */}

            <Button variant="outline" onClick={onBack || (() => navigate('/noa'))}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </section>

      {/* KPI Dashboard - Unified Panel */}
      <section className="border rounded-lg bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-5 divide-x divide-border">
          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Asset Value</div>
            <div className="text-2xl font-bold">₹{noa?.totalAssetValue?.toLocaleString("en-IN")}</div>
            <div className="text-xs text-muted-foreground">Base + GST</div>
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Monthly Rental</div>
            <div className={`text-2xl font-bold `}>
              ₹{noa?.rentalSchedule?.lines[1]?.baseRentRounded && noa?.rentalSchedule?.lines[1]?.baseRentRounded?.toLocaleString("en-IN")}
            </div>
            {/* <div className="text-xs text-muted-foreground">
              {noa?.rentalSchedule?.lines[1]?.baseRentRounded && noa?.rentalSchedule?.lines[1]?.baseRentRounded >= 0 ? "Within limit" : "Exceeds Monthly Rental"}
            </div> */}
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Funding Cap</div>
            <div className="text-2xl font-bold">₹{noa?.fundingAmountCap?.toLocaleString("en-IN")}</div>
            <div className="text-xs text-muted-foreground">{noa?.fundingLimitPercent}% limit</div>
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">PV Total</div>
            <div className="text-2xl font-bold">₹{noa?.pvTotal.toLocaleString("en-IN")}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 ">
              {noa?.selectedRentals.length} periods
              {linkedSchedule && (
                <>
                  <div className="w-[75%] mx-auto">
                    <PVCalculationModal
                      totalPV={noa?.pvTotal?.toLocaleString("en-IN")}
                      lines={linesForPV}
                      discountRate={noa?.discountRate || 0}
                      assignmentDate={noa?.assignmentDate || ""}
                      lenderFundsGST={lenderFundsGST}
                      lenderFundsTDS={lenderFundsTDS}
                    >
                      <button className="text-primary hover:underline text-xs">
                        View Details
                      </button>
                    </PVCalculationModal>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Upfront Revenue</div>
            <div className={`text-2xl font-bold ${noa?.upfrontRevenue && noa?.upfrontRevenue >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ₹{noa?.upfrontRevenue && noa?.upfrontRevenue?.toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-muted-foreground">
              {noa?.upfrontRevenue && noa?.upfrontRevenue >= 0 ? "Within limit" : "Exceeds cap"}
            </div>
          </div>

        </div>
      </section>

      {/* Main Content - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
        {/* Left Column - Assignment Details */}
        <section className="border rounded-lg p-4 flex flex-col h-full space-y-4">
          <div className="flex items-center justify-between pb-3 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Assignment Details
            </h2>
            {noa?.lenderAvatar ? (
              <Avatar className="rounded-lg" style={{ height: "36px", width: "56px" }}>
                <AvatarImage
                  src={
                    noa.lenderAvatar.startsWith("http")
                      ? noa.lenderAvatar
                      : `${import.meta.env.VITE_API_BASE_URL}/${noa.lenderAvatar}`
                  }
                  alt={noa?.lender || noa?.lenderName || "Lender"}
                  className="object-contain"
                />
              </Avatar>
            ) : null}
          </div>

          <div className="flex-1 flex flex-col space-y-6">
            {/* Key Information Grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">NoA ID</div>
                <div className="font-medium flex items-center gap-1">
                  <Hash className="h-3 w-3 text-muted-foreground" />
                  {noa?.noaId}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Schedule ID</div>
                <div className="font-medium flex items-center gap-1"> <Hash className="h-3 w-3 text-muted-foreground" />
                  <span>
                    {noa?.rentalSchedule?.rentalScheduleCode}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Lender</div>
                <div className="font-medium flex items-center gap-1">
                  <Building2 className="h-3 w-3 text-muted-foreground" />
                  {noa?.lender}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Assignment Date</div>
                <div className="font-medium flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-muted-foreground" />
                  {format(parseLocalDateInput(noa?.assignmentDate || new Date()), "MMM dd, yyyy")}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Funding Mode</div>
                <div className="font-medium">{getFundingModeLabel(noa?.fundingMode || "")}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Discount Rate</div>
                <div className="font-medium flex items-center gap-1">
                  {/* <Percent className="h-3 w-3 text-muted-foreground" /> */}
                  {noa?.discountRate}% p.a.
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Selected Periods</div>
                <div className="font-medium">
                  <Button
                    variant="link"
                    className="h-auto p-0 text-primary hover:underline"
                    onClick={() => setRentalsModalOpen(true)}
                  >
                    {noa?.selectedRentals.length} rental periods
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Base Rental</div>
                <div className="font-medium">
                  {(() => {
                    const firstRental = Array.isArray(noa?.selectedRentals)
                      ? (noa.selectedRentals[0] as any)
                      : undefined;
                    return typeof firstRental?.baseRentRounded === "number"
                      ? `₹${firstRental.baseRentRounded.toLocaleString("en-IN")}`
                      : "N/A";
                  })()}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Disbursement Date</div>
                <div className="font-medium">{noa?.disbursementDate ? format(new Date(noa?.disbursementDate || ""), "MMM dd, yyyy") : "N/A"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Disbursement Amount</div>
                <div className="font-medium"> {noa?.disbursementAmount ? "₹" + noa?.disbursementAmount?.toLocaleString("en-IN") : "N/A"}</div>
              </div>
            </div>

            {/* <Separator /> */}

            {/* NACH Setup */}
            <div className="space-y-3 mt-auto pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    NACH Setup
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Auto-debit configuration for lender-assigned rentals
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={noaNachActive
                    ? "bg-green-100 text-green-800 border-green-200"
                    : "bg-muted text-muted-foreground"
                  }
                >
                  {noaNachActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              {noaNachActive === true && noaNachStartPeriod && (
                <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded-md">
                  NACH active from Period {noaNachStartPeriod === "i" ? "Interim" : noaNachStartPeriod} •
                  Type: {noaNachType === "base_only" ? "Base Only" : "Full Amount"}
                </div>
              )}

              {!noaNachActive && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setNachModalOpen(true)}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Setup NACH
                </Button>
              )}
            </div>


          </div>
        </section>

        {/* Right Column - Funding Calculations */}
        <section className="border rounded-lg p-4 flex flex-col h-full space-y-4">
          <div className="pb-3 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Funding Calculations
            </h2>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            {fundingCalculationsLoading ? (
              <div className="flex flex-1 items-center justify-center py-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading funding calculations…
              </div>
            ) : fundingCalculations ? (
              <div className="flex-1 border rounded-lg bg-muted/20 divide-y flex flex-col justify-center">
                {[
                  {
                    label: "Disbursement Amount",
                    value: fundingCalculations.disbursementAmount,
                    icon: IndianRupee,
                  },
                  {
                    label: "Security Deposit",
                    value: fundingCalculations.securityDeposit,
                    icon: ShieldCheck,
                  },
                  {
                    label: "Customer Processing Fee",
                    value: fundingCalculations.customerProcessingFee,
                    icon: Receipt,
                  },
                  {
                    label: "Lender Processing Fee",
                    value: fundingCalculations.lenderProcessingFee,
                    icon: Receipt,
                    alwaysNegative: true,
                  },
                  {
                    label: "Interim Rental",
                    value: fundingCalculations.interimRental,
                    icon: Calendar,
                  },
                  {
                    label: "Total Asset Value",
                    value: fundingCalculations.totalAssetValue,
                    icon: Wallet,
                  },
                  {
                    label: "Upfront Margin",
                    value: fundingCalculations.upfrontMargin,
                    icon: TrendingUp,
                    signedColor: true,
                  },
                ].map(({ label, value, icon: Icon, signedColor, alwaysNegative }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between px-4 py-3"
                  >
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon className="h-4 w-4 shrink-0" />
                      {label}
                    </div>
                    <div
                      className={cn(
                        "text-sm font-semibold tabular-nums",
                        alwaysNegative && value !== 0
                          ? "text-red-600"
                          : signedColor &&
                          (value > 0
                            ? "text-green-600"
                            : value < 0
                              ? "text-red-600"
                              : "")
                      )}
                    >
                      {alwaysNegative && value !== 0
                        ? `-₹${Math.abs(value).toLocaleString("en-IN")}`
                        : formatFundingCurrency(value)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground py-6 text-center border rounded-lg bg-muted/20">
                Funding calculations are not available.
              </div>
            )}
          </div>
        </section>

      </div>

      {/* Funding Calculations */}
      {/* <section className="space-y-4">
        <div className="pb-3 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Funding Calculations
          </h2>
        </div>
        
        <div className="border rounded-lg bg-muted/30">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-x divide-border">
            <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Asset Value</div>
              <div className="text-xl font-bold">₹{noa?.totalAssetValue.toLocaleString("en-IN")}</div>
              <div className="text-xs text-muted-foreground">Base + GST</div>
            </div>
            <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Asset Value</div>
              <div className="text-xl font-bold">₹{noa?.fundingAmountCap.toLocaleString("en-IN")}</div>
              <div className="text-xs text-muted-foreground">{noa?.fundingLimitPercent}% limit</div>
            </div>
            <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Present Value</div>
              <div className="text-xl font-bold">₹{noa?.pvTotal.toLocaleString("en-IN")}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                {noa?.selectedRentals.length} periods
                {linkedSchedule && (
                    <>
                    <div className="w-[75%] mx-auto">
                    <PVCalculationModal
                      totalPV={noa?.pvTotal?.toLocaleString("en-IN")}
                      lines={linesForPV}
                      discountRate={noa?.discountRate || 0}
                      assignmentDate={noa?.assignmentDate ?? ""}
                      lenderFundsGST={lenderFundsGST}
                      lenderFundsTDS={lenderFundsTDS}
                    >
                      <button className="text-primary hover:underline text-xs">
                        View Details
                      </button>
                    </PVCalculationModal>
                    </div>
                    </>
                )}
              </div>
            </div>
            <div className="p-6 space-y-1 hover:bg-muted/50 transition-colors">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Total Funding</div>
              <div className={`text-xl font-bold ${noa?.variance && noa?.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ₹{noa?.variance?.toLocaleString("en-IN")}
              </div>
              <div className="text-xs text-muted-foreground">
                {noa?.variance && noa?.variance >= 0 ? "Within limit" : "Exceeds cap"}
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Linked Schedule */}
      <section className="space-y-4">
        <div className="pb-3 border-b">
          <h2 className="text-lg font-semibold">Linked Rental Schedule</h2>
        </div>
        {linkedSchedule ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Schedule ID</div>
              <div className="font-medium flex items-center gap-1"> <Hash className="h-3 w-3 text-muted-foreground" />
                <span>
                  {noa?.rentalSchedule?.rentalScheduleCode}
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Customer</div>
              <div className="font-medium">{noa?.customer.name}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Tenure</div>
              <div className="font-medium">{noa?.rentalSchedule?.config?.tenureMonths} Months</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Status</div>
              <Badge className={getStatusColor(noa?.rentalSchedule?.status || "")}>{noa?.rentalSchedule?.status}</Badge>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Interest Rate</div>
              <div className="font-medium flex items-center gap-1">
                {noa?.rentalSchedule?.config?.interestRatePercent * 100}% p.a.
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Funding Type</div>
              <div className="font-medium">{noa?.rentalSchedule?.config?.fundingType}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Frequency</div>
              <div className="font-medium">{noa?.rentalSchedule?.config?.frequency == 'monthly' ? 'Monthly' : 'Quarterly'}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Commencement</div>
              <div className="font-medium">{format(new Date(noa?.rentalSchedule?.config?.commencementDate || new Date()), "MMM dd, yyyy")}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Product Type</div>
              <div className="font-medium">
                {distinctProductTypes.length > 0 ? distinctProductTypes.join(", ") : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Requirement ID</div>
              <div className="font-medium">
                {distinctRequirementIds.length > 0 ? distinctRequirementIds.join(", ") : "—"}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Schedule not found</p>
          </div>
        )}
      </section>

      {/* Enhanced Audit Trail */}
      <section className="space-y-4">
        <div className="pb-3 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Audit Trail
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Created</div>
            <div className="font-medium">{format(new Date(noa?.createdAt || ""), "MMM dd, yyyy")}</div>
            {noa?.createdBy && <div className="text-xs text-muted-foreground mt-1">By: {noa?.createdBy}</div>}
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Last Updated</div>
            <div className="font-medium">{format(new Date(noa?.updatedAt || ""), "MMM dd, yyyy")}</div>
          </div>
          {noa?.approvedDate && (
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Approved</div>
              <div className="font-medium">{format(new Date(noa?.approvedDate || ""), "MMM dd, yyyy")}</div>
              {noa?.approvedBy && <div className="text-xs text-muted-foreground mt-1">By: {noa?.approvedBy}</div>}
            </div>
          )}
          {noa?.signedDate && (
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Signed</div>
              <div className="font-medium">{format(new Date(noa?.signedDate || ""), "MMM dd, yyyy")}</div>
              {noa?.signedBy && <div className="text-xs text-muted-foreground mt-1">By: {noa?.signedBy}</div>}
            </div>
          )}
          {noa?.disbursementDate && (
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Disbursement</div>
              <div className="font-medium">{format(new Date(noa?.disbursementDate || ""), "MMM dd, yyyy")}</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Current Status</div>
            <Badge variant={
              getStatusColorTwo(displayStatus)}>
              {displayStatus}
            </Badge>
          </div>
        </div>
      </section>

      {/* PDF Drawers */}
      <NoAPdfDrawer
        open={pdfDrawerOpen}
        onOpenChange={setPdfDrawerOpen}
        noa={noa || {} as NoAType}
      />

      {/* SystemGeneratedNoaDrawer removed — Download DOCX is on Visualise preview modal */}

      {/* Selected Rentals Modal */}
      <SelectedRentalsModal
        open={rentalsModalOpen}
        onOpenChange={setRentalsModalOpen}
        selectedRentals={selectedRentals}
        pvTotal={noa?.pvTotal || 0}
      />

      {noa && (
        <NoAVisualisePreviewModal
          open={visualiseOpen}
          onOpenChange={setVisualiseOpen}
          noa={noa}
          schedule={
            linkedSchedule?.lines?.length
              ? linkedSchedule
              : { lines: selectedRentals as any[] }
          }
        />
      )}

      {/* NACH Setup Modal */}
      <NoaNachSetupModal
        open={nachModalOpen}
        onOpenChange={setNachModalOpen}
        noaId={noa?.noaId || ""}
        lenderName={noa?.lender || ""}
        fundingMode={noa?.fundingMode as "A" | "B" | "C"}
        assignedRentals={selectedRentals}
        currentNachStatus={noaNachActive || false}
        currentNachStartPeriod={noaNachStartPeriod || 0}
        onSave={handleNachSave}
      />

      <NoAApproveModal
        open={approveModalOpen}
        onOpenChange={setApproveModalOpen}
        noaId={noa?.noaId || ""}
        fundingCap={noa?.fundingAmountCap || 0}
        lender={noa?.lender}
        lenderId={noa?.lenderCompanyId}
        onConfirm={handleApprovalConfirm}
        isSubmitting={isApproving}
        scheduleLines={noa?.rentalSchedule?.lines || []}
        assignmentMode={
          noa?.rentalSchedule?.assignmentMode ?? noa?.rentalSchedule?.lenderAssignment
        }
      />
    </div>
  );
}